import React, { useState } from 'react';
import { CalendarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const DateRangePicker = ({ onDateRangeChange, defaultRange = 'last3months' }) => {
  const [selectedRange, setSelectedRange] = useState(defaultRange);
  const [isCustom, setIsCustom] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Preset date ranges
  const presetRanges = {
    last7days: { label: 'Last 7 days', days: 7 },
    lastMonth: { label: 'Last month', days: 30 },
    last3months: { label: 'Last 3 months', days: 90 },
    custom: { label: 'Custom range', days: null }
  };

  // Calculate date range based on selection
  const calculateDateRange = (range, customStart = null, customEnd = null) => {
    const today = new Date();
    const endDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    if (range === 'custom' && customStart && customEnd) {
      return { startDate: customStart, endDate: customEnd };
    }
    
    const daysBack = presetRanges[range]?.days || 90;
    const startDate = new Date(today.getTime() - (daysBack * 24 * 60 * 60 * 1000))
      .toISOString().split('T')[0];
    
    return { startDate, endDate };
  };

  // Handle preset range selection
  const handlePresetChange = (range) => {
    setSelectedRange(range);
    setIsCustom(range === 'custom');
    setShowDropdown(false);
    
    if (range !== 'custom') {
      const dateRange = calculateDateRange(range);
      onDateRangeChange(dateRange.startDate, dateRange.endDate);
    }
  };

  // Handle custom date range submission
  const handleCustomDateSubmit = () => {
    if (customStartDate && customEndDate) {
      onDateRangeChange(customStartDate, customEndDate);
    }
  };

  // Initialize with default range on mount (only if no initial range is set)
  React.useEffect(() => {
    // Only initialize if no range is selected and this is the first mount
    if (!selectedRange) {
      console.log('[DateRangePicker] Initializing with default range:', defaultRange);
      setSelectedRange(defaultRange);
      const dateRange = calculateDateRange(defaultRange);
      onDateRangeChange(dateRange.startDate, dateRange.endDate);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative">
      {/* Date Range Selector */}
      <div className="flex items-center space-x-4">
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white hover:bg-gray-600 transition-colors"
          >
            <CalendarIcon className="h-4 w-4" />
            <span>{presetRanges[selectedRange]?.label || 'Select Range'}</span>
            <ChevronDownIcon className="h-4 w-4" />
          </button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div className="absolute top-full left-0 mt-1 w-48 sm-glass rounded-xl shadow-2xl z-50 overflow-hidden">
              {Object.entries(presetRanges).map(([key, range]) => (
                <button
                  key={key}
                  onClick={() => handlePresetChange(key)}
                  className={`w-full text-left px-4 py-2 hover:bg-gray-600 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                    selectedRange === key ? 'bg-blue-600 text-white' : 'text-gray-300'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Custom Date Inputs */}
        {isCustom && (
          <div className="flex items-center space-x-2">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Start Date"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="End Date"
            />
            <button
              onClick={handleCustomDateSubmit}
              disabled={!customStartDate || !customEndDate}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
            >
              Apply
            </button>
          </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default DateRangePicker;
